import { toXML } from "to-xml";
import { fromXML } from "from-xml";
import { createNodeProxy, XML, XMLList } from "./XML";

export class XMLNode {
    [key: string]: any;

    private static stringCache = new Map<string, string>();
    private _data: any;
    private _parent: any;

    constructor(value: any, parent: any = null) {
        this._data = value; // value can be an empty string
        this._parent = parent;
    }

    public elements(name = "*"): XMLList {
        if (name !== "*") {
            return this.child(name);
        }

        for (var i in this._data) {
            let element = this._data[i];
            if (Array.isArray(element)) {
                return element.map(el => createNodeProxy(el, this));
            }
        }

        throw new Error("elements() can only be used when children is an array.");
    }

    public child(tagName: string): XML {
        return createNodeProxy(this._data[tagName], this);
    }

    public children(): XML[] {
        let nodes: XML[] = [];

        for (let i in this._data) {
            if ( this._data.hasOwnProperty(i)) {
                if (this._data[i] instanceof Array) {
                    nodes = nodes.concat(this._data[i]);
                } else if (i.indexOf("@") !== 0) {
                    nodes.push(this._data[i]);
                }
            }
        }

        return nodes.map(node => createNodeProxy(node, this));
    }

    public appendChild(child: string): void {
        let childXML: any = fromXML(child);
        for (let childProperty in childXML) {
            if (childProperty.match(/[A-Za-z\-]+/)) {
                // ignore private properties
                if (!this._data) {
                    this._data = {};
                }
                if (!this._data[childProperty]) {
                    this._data[childProperty] = childXML[childProperty];
                } else if (this._data[childProperty] instanceof Array) {
                    this._data[childProperty].push(childXML[childProperty]);
                } else {
                    let origin = Object.assign({}, this._data[childProperty]);
                    this._data[childProperty] = [];
                    this._data[childProperty].push(origin);
                    this._data[childProperty].push(childXML[childProperty]);
                }
            }
        }
    }

    // attribute() was modified by the HTML5-team to add support for returning int values
    public attribute(name: string): string {
        let atName = XMLNode.stringCache.get(name);
        if (!atName) {
            XMLNode.stringCache.set(name, atName = `@${name}`);
        }
        return this._data[atName] || "";
    }

    public text() {
        throw new Error("XMLNode.text not implemented.");
    }

    public attributes(): string[] {
        let attributes = [];
        for (let attr in this._data) {
            if (attr.indexOf("@") === 0) {
                attributes.push(this._data[attr]);
            }
        }
        return attributes;
    }


    public length() {
        if (Array.isArray(this._data)) {
            return this._data.length;
        } else {
            return this.elements().length;
        }
    }

    public get parent() {
        return this._parent;
    }

    public data() {
        return this._data;
    }

    public clearAllNodes() {
        this._data = this._data instanceof Array ? [] : {};
    }

    public toJSON() {
        return JSON.stringify(this._data);
    }

    public toString() {
        return toXML(this._data);
    }

}
